import ApisServiceApp from './ApisServiceApp'
import { apiRequest } from '@/utils/apiRequestUtils'
import { blobResponseConfig, ensureExcelBlob, postMultipartFile } from '@/utils/blobApiUtils'
import { normalizeDetailResponse, normalizeListResponse } from '@/utils/apiResponseUtils'
import type {
  IKhachHang,
  IKhachHangPayload,
  IKhachHangDiaChiNhanHangPayload,
  IKhachHangThongTinXuatHoaDonPayload,
  KhachHangId,
  KhachHangListSummary,
  KhachHangTrangThai,
} from '@/interfaces/khachHangInterfaces'
import queryString from 'query-string'
import { hoTenFromHoSo } from '@/utils/hoTenUtils'

const PREFIX = '/khach-hang'

function toStrArr(v: unknown): string[] | undefined {
  if (!Array.isArray(v)) return undefined
  return v.map((x) => String(x))
}

function extractNhomKHIds(raw: Record<string, unknown>): string[] | undefined {
  const direct = toStrArr(raw.nhomKHIds) ?? toStrArr(raw.nhomKhachHangIds)
  if (direct?.length) return direct

  const nhomKHs = raw.nhomKHs
  if (!Array.isArray(nhomKHs)) return undefined

  const ids = nhomKHs
    .map((row) => {
      const r = row as Record<string, unknown>
      if (r.nhomKHId != null && String(r.nhomKHId).trim()) return String(r.nhomKHId)
      const nested = r.nhomKH as Record<string, unknown> | null | undefined
      if (nested?.id != null && String(nested.id).trim()) return String(nested.id)
      return ''
    })
    .filter(Boolean)

  return ids.length ? ids : undefined
}

function extractNhomKHLabels(raw: Record<string, unknown>): string[] | undefined {
  const nhomKHs = raw.nhomKHs
  if (!Array.isArray(nhomKHs)) return undefined

  const labels = nhomKHs
    .map((row) => {
      const nested = (row as Record<string, unknown>).nhomKH as Record<string, unknown> | null | undefined
      if (!nested) return ''
      const ten = nested.tenNhom != null ? String(nested.tenNhom) : ''
      const ma = nested.ma != null ? String(nested.ma) : ''
      if (ten && ma) return `${ten} (${ma})`
      return ten || ma
    })
    .filter(Boolean)

  return labels.length ? labels : undefined
}

function extractNhanVienPhuTrach(
  raw: Record<string, unknown>,
): IKhachHang['nhanVienPhuTrach'] {
  const nv = raw.nhanVienPhuTrach as Record<string, unknown> | null | undefined
  if (!nv || typeof nv !== 'object') return null
  const id = Number(nv.id)
  if (!Number.isFinite(id)) return null
  const hoSo = (nv.hoSo as Record<string, unknown> | null | undefined) ?? null
  const hoTen = hoTenFromHoSo(
    hoSo
      ? {
          firstName: hoSo.firstName != null ? String(hoSo.firstName) : null,
          lastName: hoSo.lastName != null ? String(hoSo.lastName) : null,
        }
      : null,
  )
  return {
    id,
    username: String(nv.username ?? ''),
    hoTen: hoTen || null,
  }
}

function extractDiaChiNhanHangs(
  raw: Record<string, unknown>,
): IKhachHangDiaChiNhanHangPayload[] | undefined {
  const arr = raw.diaChiNhanHangs
  if (!Array.isArray(arr)) return undefined
  const items = arr
    .map((row) => {
      const r = row as Record<string, unknown>
      const item: IKhachHangDiaChiNhanHangPayload = {
        tenNguoiNhan: String(r.tenNguoiNhan ?? '').trim(),
      }
      if (r.id != null && String(r.id).trim()) item.id = String(r.id)
      if (r.dienThoai != null) item.dienThoai = String(r.dienThoai)
      if (r.diaChi != null) item.diaChi = String(r.diaChi)
      if (r.tinhThanh != null) item.tinhThanh = String(r.tinhThanh)
      if (r.phuongXa != null) item.phuongXa = String(r.phuongXa)
      if (r.placeId != null) item.placeId = String(r.placeId)
      if (r.viDo != null && Number.isFinite(Number(r.viDo))) item.viDo = Number(r.viDo)
      if (r.kinhDo != null && Number.isFinite(Number(r.kinhDo))) item.kinhDo = Number(r.kinhDo)
      if (typeof r.macDinh === 'boolean') item.macDinh = r.macDinh
      return item.tenNguoiNhan ? item : null
    })
    .filter((x): x is IKhachHangDiaChiNhanHangPayload => Boolean(x))
  return items.length ? items : undefined
}

function extractTaiKhoans(
  r: Record<string, unknown>,
): IKhachHangThongTinXuatHoaDonPayload['taiKhoans'] {
  const arr = r.taiKhoans
  if (Array.isArray(arr) && arr.length > 0) {
    return arr
      .map((row) => {
        const s = row as Record<string, unknown>
        const soTaiKhoan = String(s.soTaiKhoan ?? '').trim()
        if (!soTaiKhoan) return null
        const item: NonNullable<IKhachHangThongTinXuatHoaDonPayload['taiKhoans']>[number] = {
          soTaiKhoan,
        }
        if (s.id != null && String(s.id).trim()) item.id = String(s.id)
        if (s.tenNganHang != null) item.tenNganHang = String(s.tenNganHang)
        if (s.tenChuTaiKhoan != null) item.tenChuTaiKhoan = String(s.tenChuTaiKhoan)
        if (typeof s.macDinh === 'boolean') item.macDinh = s.macDinh
        if (s.thuTu != null && Number.isFinite(Number(s.thuTu))) item.thuTu = Number(s.thuTu)
        return item
      })
      .filter((x): x is NonNullable<typeof x> => Boolean(x))
  }
  // Legacy: 1 cặp nganHang + soTaiKhoanNganHang → 1 STK
  const soTk = String(r.soTaiKhoanNganHang ?? '').trim()
  if (!soTk) return []
  return [
    {
      soTaiKhoan: soTk,
      tenNganHang: String(r.nganHang ?? '').trim() || undefined,
      macDinh: true,
      thuTu: 0,
    },
  ]
}

function extractThongTinXuatHoaDons(
  raw: Record<string, unknown>,
): IKhachHangThongTinXuatHoaDonPayload[] | undefined {
  const arr = raw.thongTinXuatHoaDons
  if (!Array.isArray(arr)) return undefined
  const items = arr
    .map((row) => {
      const r = row as Record<string, unknown>
      const item: IKhachHangThongTinXuatHoaDonPayload = {
        ten: String(r.ten ?? '').trim(),
      }
      if (r.id != null && String(r.id).trim()) item.id = String(r.id)
      if (r.tenNguoiMua != null) item.tenNguoiMua = String(r.tenNguoiMua)
      if (r.maSoThue != null) item.maSoThue = String(r.maSoThue)
      if (r.diaChiHoaDon != null) item.diaChiHoaDon = String(r.diaChiHoaDon)
      if (r.tinhThanhHoaDon != null) item.tinhThanhHoaDon = String(r.tinhThanhHoaDon)
      if (r.phuongXaHoaDon != null) item.phuongXaHoaDon = String(r.phuongXaHoaDon)
      if (r.soCCCD != null) item.soCCCD = String(r.soCCCD)
      if (r.soHoChieu != null) item.soHoChieu = String(r.soHoChieu)
      if (r.emailHoaDon != null) item.emailHoaDon = String(r.emailHoaDon)
      if (r.soDienThoaiHoaDon != null) item.soDienThoaiHoaDon = String(r.soDienThoaiHoaDon)
      if (r.nganHang != null) item.nganHang = String(r.nganHang)
      if (r.soTaiKhoanNganHang != null)
        item.soTaiKhoanNganHang = String(r.soTaiKhoanNganHang)
      item.taiKhoans = extractTaiKhoans(r)
      if (typeof r.macDinh === 'boolean') item.macDinh = r.macDinh
      return item.ten ? item : null
    })
    .filter((x): x is IKhachHangThongTinXuatHoaDonPayload => Boolean(x))
  return items.length ? items : undefined
}

const trimOrUndef = (v: string | null | undefined): string | undefined => {
  const t = v?.trim()
  return t ? t : undefined
}

/** null → gửi null (xóa giới hạn); undefined → bỏ qua field. */
function toOptionalNonNegative(value: number | null | undefined): number | null | undefined {
  if (value === undefined) return undefined
  if (value === null || !Number.isFinite(Number(value))) return null
  return Math.max(0, Number(value))
}

function toNullableNumber(value: unknown): number | null {
  if (value == null || value === '') return null
  const n = Number(value)
  return Number.isFinite(n) ? n : null
}

/** Body JSON cho `tao-khach-hang` / `cap-nhat-khach-hang` (BE dùng `@Body()`, không multipart). */
function toKhachHangPayload(body: IKhachHangPayload): Record<string, unknown> {
  const gioiTinh =
    body.gioiTinh === 'nam' || body.gioiTinh === 'nu' ? body.gioiTinh : undefined
  const loai =
    body.loaiKhachHang === 'caNhan' || body.loaiKhachHang === 'congTy'
      ? body.loaiKhachHang
      : undefined
  const nhomKHIds = body.nhomKHIds
    ?.filter((id) => String(id).trim())
    .map((id) => String(id).trim())

  // Sub-resource: undefined → BE bỏ qua; array (kể cả rỗng) → BE sync.
  const diaChiNhanHangs = Array.isArray(body.diaChiNhanHangs)
    ? body.diaChiNhanHangs.map((d) => {
        const out: Record<string, unknown> = {
          tenNguoiNhan: String(d.tenNguoiNhan ?? '').trim(),
          dienThoai: trimOrUndef(d.dienThoai),
          diaChi: trimOrUndef(d.diaChi),
          tinhThanh: trimOrUndef(d.tinhThanh),
          phuongXa: trimOrUndef(d.phuongXa),
          placeId: d.placeId != null && String(d.placeId).trim() ? String(d.placeId) : null,
          viDo:
            d.viDo != null && Number.isFinite(Number(d.viDo)) ? Number(d.viDo) : null,
          kinhDo:
            d.kinhDo != null && Number.isFinite(Number(d.kinhDo))
              ? Number(d.kinhDo)
              : null,
          macDinh: d.macDinh === true,
        }
        if (d.id) out.id = d.id
        return out
      })
    : undefined

  const thongTinXuatHoaDons = Array.isArray(body.thongTinXuatHoaDons)
    ? body.thongTinXuatHoaDons.map((d) => {
        const taiKhoans = (d.taiKhoans ?? [])
          .map((stk, idx) => {
            const soTaiKhoan = String(stk.soTaiKhoan ?? '').trim()
            if (!soTaiKhoan) return null
            const row: Record<string, unknown> = {
              soTaiKhoan,
              tenNganHang: trimOrUndef(stk.tenNganHang),
              tenChuTaiKhoan: trimOrUndef(stk.tenChuTaiKhoan),
              macDinh: stk.macDinh === true,
              thuTu: typeof stk.thuTu === 'number' ? stk.thuTu : idx,
            }
            if (stk.id) row.id = stk.id
            return row
          })
          .filter((x): x is Record<string, unknown> => Boolean(x))
        // Snapshot mặc định từ STK đầu/macDinh (tương thích cột cũ)
        const def =
          taiKhoans.find((x) => x.macDinh === true) ?? taiKhoans[0] ?? null
        const out: Record<string, unknown> = {
          ten: String(d.ten ?? '').trim(),
          tenNguoiMua: trimOrUndef(d.tenNguoiMua),
          maSoThue: trimOrUndef(d.maSoThue),
          diaChiHoaDon: trimOrUndef(d.diaChiHoaDon),
          tinhThanhHoaDon: trimOrUndef(d.tinhThanhHoaDon),
          phuongXaHoaDon: trimOrUndef(d.phuongXaHoaDon),
          soCCCD: trimOrUndef(d.soCCCD),
          soHoChieu: trimOrUndef(d.soHoChieu),
          emailHoaDon: trimOrUndef(d.emailHoaDon),
          soDienThoaiHoaDon: trimOrUndef(d.soDienThoaiHoaDon),
          nganHang: def
            ? trimOrUndef(String(def.tenNganHang ?? ''))
            : trimOrUndef(d.nganHang),
          soTaiKhoanNganHang: def
            ? trimOrUndef(String(def.soTaiKhoan ?? ''))
            : trimOrUndef(d.soTaiKhoanNganHang),
          taiKhoans,
          macDinh: d.macDinh === true,
        }
        if (d.id) out.id = d.id
        return out
      })
    : undefined

  const payload: Record<string, unknown> = {
    maKhachHang: trimOrUndef(body.maKhachHang),
    tenKhachHang: String(body.tenKhachHang ?? '').trim(),
    dienThoai1: trimOrUndef(body.dienThoai1),
    dienThoai2: trimOrUndef(body.dienThoai2),
    sinhNhat: trimOrUndef(body.sinhNhat),
    gioiTinh,
    email: trimOrUndef(body.email),
    facebook: trimOrUndef(body.facebook),
    diaChi: trimOrUndef(body.diaChi),
    tinhThanh: trimOrUndef(body.tinhThanh),
    phuongXa: trimOrUndef(body.phuongXa),
    ghiChu: trimOrUndef(body.ghiChu),
    loaiKhachHang: loai,
    nhanVienPhuTrachId:
      typeof body.nhanVienPhuTrachId === 'number'
        ? body.nhanVienPhuTrachId
        : body.nhanVienPhuTrachId === null
          ? null
          : undefined,
    dinhMucCongNo: toOptionalNonNegative(body.dinhMucCongNo),
    ngayCanDoiNo:
      body.ngayCanDoiNo === undefined ? undefined : body.ngayCanDoiNo?.slice(0, 10) || null,
    ngayDoiNoHangThang:
      body.ngayDoiNoHangThang === undefined ? undefined : body.ngayDoiNoHangThang ?? null,
  }
  if (nhomKHIds?.length) payload.nhomKHIds = nhomKHIds
  if (diaChiNhanHangs) payload.diaChiNhanHangs = diaChiNhanHangs
  if (thongTinXuatHoaDons) payload.thongTinXuatHoaDons = thongTinXuatHoaDons
  return payload
}

export function mapKhachHangFromApi(raw: Record<string, unknown>): IKhachHang {
  const tt = raw.trangThai
  let trangThai: IKhachHang['trangThai'] = null
  if (tt === 'dangHoatDong' || tt === 'ngungHoatDong') trangThai = tt
  const gt = raw.gioiTinh
  let gioiTinh: IKhachHang['gioiTinh'] = null
  if (gt === 'nam' || gt === 'nu') gioiTinh = gt

  return {
    id: String(raw.id ?? ''),
    maKhachHang: String(raw.maKhachHang ?? ''),
    tenKhachHang: String(raw.tenKhachHang ?? ''),
    dienThoai1: raw.dienThoai1 != null ? String(raw.dienThoai1) : null,
    dienThoai2: raw.dienThoai2 != null ? String(raw.dienThoai2) : null,
    sinhNhat: raw.sinhNhat != null ? String(raw.sinhNhat) : null,
    gioiTinh,
    email: raw.email != null ? String(raw.email) : null,
    facebook: raw.facebook != null ? String(raw.facebook) : null,
    diaChi: raw.diaChi != null ? String(raw.diaChi) : null,
    tinhThanh: raw.tinhThanh != null ? String(raw.tinhThanh) : null,
    phuongXa: raw.phuongXa != null ? String(raw.phuongXa) : null,
    ghiChu: raw.ghiChu != null ? String(raw.ghiChu) : null,
    loaiKhachHang: raw.loaiKhachHang != null ? String(raw.loaiKhachHang) : null,
    nhanVienPhuTrachId:
      raw.nhanVienPhuTrachId != null ? Number(raw.nhanVienPhuTrachId) : null,
    nhanVienPhuTrach: extractNhanVienPhuTrach(raw),
    nhomKHIds: extractNhomKHIds(raw),
    nhomKHLabels: extractNhomKHLabels(raw),
    diaChiNhanHangs: extractDiaChiNhanHangs(raw),
    thongTinXuatHoaDons: extractThongTinXuatHoaDons(raw),
    trangThai,
    ngayTao: raw.ngayTao != null ? String(raw.ngayTao) : null,
    ngayCapNhat: raw.ngayCapNhat != null ? String(raw.ngayCapNhat) : null,
    tongBan: typeof raw.tongBan === 'number' ? raw.tongBan : raw.tongBan != null ? Number(raw.tongBan) : null,
    noHienTai: typeof raw.noHienTai === 'number' ? raw.noHienTai : raw.noHienTai != null ? Number(raw.noHienTai) : null,
    tongBanTruTraHang:
      typeof raw.tongBanTruTraHang === 'number'
        ? raw.tongBanTruTraHang
        : raw.tongBanTruTraHang != null
          ? Number(raw.tongBanTruTraHang)
          : null,
    dinhMucCongNo: toNullableNumber(raw.dinhMucCongNo),
    ngayCanDoiNo: raw.ngayCanDoiNo ? String(raw.ngayCanDoiNo).slice(0, 10) : null,
    ngayDoiNoHangThang: toNullableNumber(raw.ngayDoiNoHangThang),
  }
}

function khachHangListParamsFromSearch(search: string): Record<string, string | number> {
  const q = queryString.parse(search.replace(/^\?/, ''))
  const out: Record<string, string | number> = {}
  const page = Number(q.page)
  const pageSize = Number(q.pageSize)
  if (Number.isFinite(page) && page > 0) out.page = page
  if (Number.isFinite(pageSize) && pageSize > 0) out.pageSize = pageSize
  const searchVal = q.search
  if (typeof searchVal === 'string' && searchVal.trim()) out.search = searchVal.trim()
  const maKhachHang = q.maKhachHang
  if (typeof maKhachHang === 'string' && maKhachHang.trim()) out.maKhachHang = maKhachHang.trim()
  const tenKhachHang = q.tenKhachHang
  if (typeof tenKhachHang === 'string' && tenKhachHang.trim()) out.tenKhachHang = tenKhachHang.trim()
  const dienThoai = q.dienThoai
  if (typeof dienThoai === 'string' && dienThoai.trim()) out.dienThoai = dienThoai.trim()
  const email = q.email
  if (typeof email === 'string' && email.trim()) out.email = email.trim()
  const maNhomKhachHang = q.maNhomKhachHang
  if (typeof maNhomKhachHang === 'string' && maNhomKhachHang.trim()) {
    out.maNhomKhachHang = maNhomKhachHang.trim()
  }
  const tenNhomKhachHang = q.tenNhomKhachHang
  if (typeof tenNhomKhachHang === 'string' && tenNhomKhachHang.trim()) {
    out.tenNhomKhachHang = tenNhomKhachHang.trim()
  }
  const trangThai = q.trangThai
  if (trangThai === 'dangHoatDong' || trangThai === 'ngungHoatDong') out.trangThai = trangThai
  const nhomKHId = q.nhomKHId
  if (typeof nhomKHId === 'string' && nhomKHId.trim()) out.nhomKHId = nhomKHId.trim()
  const loaiKhachHang = q.loaiKhachHang
  if (loaiKhachHang === 'caNhan' || loaiKhachHang === 'congTy') out.loaiKhachHang = loaiKhachHang
  const gioiTinh = q.gioiTinh
  if (gioiTinh === 'nam' || gioiTinh === 'nu') out.gioiTinh = gioiTinh
  const khuVuc = q.khuVuc
  if (typeof khuVuc === 'string' && khuVuc.trim()) out.khuVuc = khuVuc.trim()
  const nhanVienPhuTrachId = q.nhanVienPhuTrachId
  if (typeof nhanVienPhuTrachId === 'string' && nhanVienPhuTrachId.trim()) {
    const num = Number(nhanVienPhuTrachId)
    if (Number.isFinite(num) && num > 0) out.nhanVienPhuTrachId = num
  }
  const maNhanVienPhuTrach = q.maNhanVienPhuTrach
  if (typeof maNhanVienPhuTrach === 'string' && maNhanVienPhuTrach.trim()) {
    out.maNhanVienPhuTrach = maNhanVienPhuTrach.trim()
  }
  const tinhTrangCongNo = q.tinhTrangCongNo
  if (tinhTrangCongNo === 'conNo' || tinhTrangCongNo === 'hetNo') {
    out.tinhTrangCongNo = tinhTrangCongNo
  }
  const sort = q.sort
  if (
    sort === 'maKhachHang' ||
    sort === 'tenKhachHang' ||
    sort === 'ngayTao' ||
    sort === 'trangThai' ||
    sort === 'noHienTai' ||
    sort === 'tongBan'
  ) {
    out.sort = sort
  }
  const sortDir = q.sortDir
  if (sortDir === 'asc' || sortDir === 'desc') out.sortDir = sortDir
  return out
}

function mapKhachHangListSummary(raw?: Record<string, number>): KhachHangListSummary | undefined {
  if (!raw) return undefined
  return {
    noHienTai: Number(raw.tongNoHienTai ?? raw.noHienTai) || 0,
    tongBan: Number(raw.tongBan) || 0,
    tongBanTruTraHang: Number(raw.tongBanTruTraHang) || 0,
  }
}

export function fetchKhachHangList(search: string) {
  const params = khachHangListParamsFromSearch(search)
  return apiRequest(
    ApisServiceApp.get(`${PREFIX}/danh-sach-khach-hang`, { params }).then((res: unknown) => {
      const { data, paging, summary } = normalizeListResponse(
        res as Parameters<typeof normalizeListResponse>[0],
      )
      return {
        data: data.map((r) => mapKhachHangFromApi(r as Record<string, unknown>)),
        paging,
        summary: mapKhachHangListSummary(summary),
      }
    }),
  )
}

const chiLaySo = (s: string) => s.replace(/\D/g, '')

/** KH khác có SĐT1/SĐT2 trùng (so theo chữ số) — dùng cảnh báo trước khi lưu. */
export async function timKhachHangTrungSdt(
  dienThoai: string,
  boQuaId?: KhachHangId,
): Promise<IKhachHang[]> {
  const so = chiLaySo(dienThoai)
  if (!so) return []
  const res = await ApisServiceApp.get(`${PREFIX}/danh-sach-khach-hang`, {
    params: { dienThoai: dienThoai.trim(), page: 1, pageSize: 20 },
  })
  const { data } = normalizeListResponse(res as Parameters<typeof normalizeListResponse>[0])
  return data
    .map((r) => mapKhachHangFromApi(r as Record<string, unknown>))
    .filter(
      (k) =>
        String(k.id) !== String(boQuaId ?? '') &&
        [k.dienThoai1, k.dienThoai2].some((p) => chiLaySo(String(p ?? '')) === so),
    )
}

export function fetchKhachHangSelect() {
  return apiRequest(
    ApisServiceApp.get(`${PREFIX}/danh-sach-khach-hang-select`).then((res: unknown) => {
      const { data } = normalizeListResponse(res as Parameters<typeof normalizeListResponse>[0])
      return data.map((r) => mapKhachHangFromApi(r as Record<string, unknown>))
    }),
  )
}

/** Gợi ý dropdown lọc — tối đa 500 bản ghi đầu (mã, tên, SĐT, email). */
export function fetchKhachHangFilterOptions() {
  return fetchKhachHangList('?page=1&pageSize=500').then((res) => {
    const data = (res as { data?: IKhachHang[] })?.data ?? []
    return Array.isArray(data) ? data : []
  })
}

export function fetchKhachHangDetail(id: KhachHangId) {
  return apiRequest(
    ApisServiceApp.get(`${PREFIX}/${id}/chi-tiet-khach-hang`).then((res: unknown) => {
      const raw = normalizeDetailResponse<Record<string, unknown>>(res as { data?: Record<string, unknown> })
      return { data: mapKhachHangFromApi(raw) }
    }),
  )
}

export function taoKhachHang(body: IKhachHangPayload) {
  return apiRequest(ApisServiceApp.post(`${PREFIX}/tao-khach-hang`, toKhachHangPayload(body)))
}

export function capNhatKhachHang(id: KhachHangId, body: IKhachHangPayload) {
  return apiRequest(
    ApisServiceApp.put(`${PREFIX}/${id}/cap-nhat-khach-hang`, toKhachHangPayload(body)),
  )
}

export function xoaKhachHang(id: KhachHangId) {
  return apiRequest(ApisServiceApp.delete(`${PREFIX}/${id}/xoa-khach-hang`))
}

export type XoaNhieuKhachHangResult = {
  ok: number
  failed: Array<{ id: string; message: string }>
}

/** Xóa nhiều khách hàng — gọi `DELETE .../xoa-khach-hang` từng id (không có API bulk). */
export async function xoaNhieuKhachHang(ids: KhachHangId[]): Promise<XoaNhieuKhachHangResult> {
  const failed: XoaNhieuKhachHangResult['failed'] = []
  let ok = 0
  for (const id of ids) {
    try {
      await xoaKhachHang(id)
      ok += 1
    } catch (e) {
      failed.push({
        id: String(id),
        message: (e as { message?: string })?.message ?? '',
      })
    }
  }
  return { ok, failed }
}

export function chuyenTrangThaiKhachHang(id: KhachHangId, trangThai: KhachHangTrangThai) {
  return apiRequest(ApisServiceApp.patch(`${PREFIX}/${id}/chuyen-trang-thai-khach-hang`, { trangThai }))
}

export type LoaiNhapKhachHang =
  | 'kh'
  | 'diaChi'
  | 'cty'
  | 'taiKhoan'
  | 'ctyTaiKhoan'
  | 'toHop'

const TEMPLATE_FILE_BY_LOAI: Record<LoaiNhapKhachHang, string> = {
  kh: 'mau_nhap_khach_hang.xlsx',
  diaChi: 'mau_nhap_thong_tin_giao_hang.xlsx',
  cty: 'mau_nhap_cty_lay_thue.xlsx',
  taiKhoan: 'mau_nhap_tai_khoan.xlsx',
  ctyTaiKhoan: 'mau_nhap_cty_va_tai_khoan.xlsx',
  toHop: 'mau_nhap_khach_hang_tong_hop.xlsx',
}

export async function taiFileMauNhapKhachHangBlob(loai: LoaiNhapKhachHang = 'toHop'): Promise<Blob> {
  const blob = (await apiRequest(
    ApisServiceApp.get<Blob>(
      `${PREFIX}/tai-file-mau-nhap-khach-hang`,
      { ...blobResponseConfig, params: { loai } },
    ),
  )) as unknown as Blob
  return ensureExcelBlob(blob)
}

export function templateFileNameNhapKhachHang(loai: LoaiNhapKhachHang = 'toHop'): string {
  return TEMPLATE_FILE_BY_LOAI[loai]
}

/** @deprecated Dùng `nhapDanhSachKhachHang` (FE parse Excel → JSON). */
export function nhapKhachHangTuExcel(file: File, loaiNhap: LoaiNhapKhachHang = 'toHop') {
  return apiRequest(
    postMultipartFile(`${PREFIX}/nhap-khach-hang-tu-excel`, file, { loaiNhap }),
  )
}

/** Mỗi lô ~300 dòng; import 2500+ dòng chia nhiều request. */
const NHAP_KHACH_HANG_TIMEOUT_MS = 240_000

/** FE đã parse Excel → POST JSON `khach-hang-nhap-danh-sach`. */
export function nhapDanhSachKhachHang(body: {
  loaiNhap: LoaiNhapKhachHang
  khachHang?: unknown[]
  diaChi?: unknown[]
  cty?: unknown[]
  taiKhoan?: unknown[]
  /** Đã tắt — BE bỏ qua; giữ type để tương thích caller cũ. */
  capNhatDuNoCuoi?: boolean
  choPhepTrungEmail?: boolean
}) {
  return apiRequest(
    ApisServiceApp.post(
      `${PREFIX}/khach-hang-nhap-danh-sach`,
      { ...body, capNhatDuNoCuoi: false },
      {
        timeout: NHAP_KHACH_HANG_TIMEOUT_MS,
      },
    ),
  )
}
